import { FormEvent, useMemo, useState } from "react";
import { RECORD_TYPES, RecordType } from "../lib/recordTypes";

// IPv4 → "1.2.3.4" → "4.3.2.1.in-addr.arpa"
// IPv6 → expand to nibbles → "<reversed-nibbles>.ip6.arpa"
// Returns null if input doesn't look like a bare IP.
function toReverseArpa(input: string): string | null {
  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(input);
  if (v4) {
    const parts = v4.slice(1, 5).map((p) => Number(p));
    if (parts.every((n) => n >= 0 && n <= 255)) {
      return parts.slice().reverse().join(".") + ".in-addr.arpa";
    }
  }
  if (input.includes(":")) {
    const expanded = expandIPv6(input);
    if (expanded) {
      return expanded.split("").reverse().join(".") + ".ip6.arpa";
    }
  }
  return null;
}

function expandIPv6(ip: string): string | null {
  if (!/^[0-9a-f:]+$/i.test(ip)) return null;
  const parts = ip.split("::");
  if (parts.length > 2) return null;
  const left = parts[0] ? parts[0].split(":") : [];
  const right = parts[1] !== undefined && parts[1] !== "" ? parts[1].split(":") : [];
  const missing = 8 - (left.length + right.length);
  if (missing < 0) return null;
  const groups = [...left, ...Array(missing).fill("0"), ...right];
  if (groups.length !== 8) return null;
  return groups.map((g) => g.padStart(4, "0")).join("").toLowerCase();
}

type FormMode = "default" | "dmarc" | "spf" | "dkim";

function readFormMode(): FormMode {
  if (typeof document === "undefined") return "default";
  const meta = document.querySelector('meta[name="x-form-mode"]') as HTMLMetaElement | null;
  const v = meta?.content?.toLowerCase();
  if (v === "dmarc" || v === "spf" || v === "dkim") return v;
  return "default";
}

type Props = {
  initialHost?: string;
  initialType?: RecordType;
  onSubmit: (host: string, type: RecordType) => void;
  busy: boolean;
};

export function LookupForm({ initialHost = "", initialType = "A", onSubmit, busy }: Props) {
  const formMode = useMemo(readFormMode, []);
  const [host, setHost] = useState(initialHost);
  const [selector, setSelector] = useState("");
  const [type, setType] = useState<RecordType>(initialType);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    let trimmed = host.trim().toLowerCase().replace(/\.$/, "");
    if (!trimmed) return;

    // PTR convenience: bare IPv4/IPv6 → in-addr.arpa / ip6.arpa
    if (type === "PTR") {
      const arpa = toReverseArpa(trimmed);
      if (arpa) trimmed = arpa;
    }

    // DMARC convenience: prepend _dmarc. if not already present
    if (formMode === "dmarc" && !trimmed.startsWith("_dmarc.")) {
      trimmed = `_dmarc.${trimmed}`;
    }

    // DKIM convenience: build <selector>._domainkey.<domain>
    if (formMode === "dkim") {
      const sel = selector.trim().toLowerCase().replace(/\._domainkey.*$/i, "");
      if (!sel) return;
      // If user already pasted the full _domainkey form, leave it alone
      if (!trimmed.includes("._domainkey.")) {
        trimmed = `${sel}._domainkey.${trimmed}`;
      }
    }

    onSubmit(trimmed, type);
  };

  const hostPlaceholder = (() => {
    if (formMode === "dmarc") return "example.com  (we'll add _dmarc. for you)";
    if (formMode === "dkim") return "example.com";
    if (formMode === "spf") return "example.com  (we'll show TXT records — look for v=spf1)";
    if (type === "PTR") return "8.8.8.8 or 8.8.8.8.in-addr.arpa";
    return "example.com";
  })();

  return (
    <form onSubmit={submit} className="lookup-form">
      {formMode === "dkim" ? (
        <input
          type="text"
          placeholder="selector  (e.g., google, k1)"
          value={selector}
          onChange={(e) => setSelector(e.target.value)}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          aria-label="DKIM selector"
          className="dkim-selector"
        />
      ) : null}
      <input
        type="text"
        placeholder={hostPlaceholder}
        value={host}
        onChange={(e) => setHost(e.target.value)}
        autoFocus
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        aria-label="Domain or hostname"
      />
      {formMode === "default" ? (
        <select
          value={type}
          onChange={(e) => setType(e.target.value as RecordType)}
          aria-label="DNS record type"
        >
          {RECORD_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      ) : null}
      <button
        type="submit"
        disabled={busy || !host.trim() || (formMode === "dkim" && !selector.trim())}
      >
        {busy ? "Checking…" : "Check"}
      </button>
    </form>
  );
}
